/**
 * The store's state: the token, the catalogue, the player's basket and what they are
 * doing with it. One basket per browser, its ident kept in localStorage until it is paid
 * or the player starts over.
 */

import { create } from 'zustand'

import {
  headless,
  readToken,
  StoreError,
  type Basket,
  type Category,
  type Headless,
  type Package,
  type Webstore,
} from './api'
import { absolute, navigate } from './router'

const BASKET_KEY = 'unity-store:basket'
/** A package picked before signing in, added when Tebex sends the player back. */
const PENDING_KEY = 'unity-store:pending'

type Buyable = 'single' | 'subscription'

interface Pending {
  id: number
  type: Buyable
}

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key)
  } catch {
    return null
  }
}

function write(storage: () => Storage, key: string, value: string | null) {
  try {
    if (value === null) storage().removeItem(key)
    else storage().setItem(key, value)
  } catch {
    // A browser that keeps nothing still shops; it just forgets on reload.
  }
}

const local = () => window.localStorage
const session = () => window.sessionStorage

function message(error: unknown): string {
  return error instanceof Error ? error.message : 'Qualcosa è andato storto.'
}

interface StoreState {
  /** undefined while asking the panel; null when the store is closed. */
  token: string | null | undefined
  failed: boolean
  api: Headless | null
  webstore: Webstore | null
  categories: Category[] | undefined
  basket: Basket | null
  /** What is being done right now: "add:12", "checkout", "code"… */
  busy: string | null
  drawer: boolean
  /** The package waiting for the player to sign in. */
  signIn: { pkg: Package | null; type: Buyable } | null
  notice: { text: string; tone: 'ok' | 'error' } | null

  boot: () => Promise<void>
  setDrawer: (open: boolean) => void
  closeSignIn: () => void
  say: (text: string, tone?: 'ok' | 'error') => void
  add: (pkg: Package, type: Buyable) => Promise<void>
  goSignIn: () => Promise<void>
  remove: (id: number) => Promise<void>
  setQuantity: (id: number, quantity: number) => Promise<void>
  applyCode: (kind: 'coupon' | 'creator', code: string) => Promise<boolean>
  dropCode: (kind: 'coupon' | 'creator', code: string) => Promise<void>
  startOver: () => void
  checkout: () => Promise<void>
}

let noticeTimer: ReturnType<typeof setTimeout> | undefined
let booted = false

export const useStore = create<StoreState>()((set, get) => {
  const forgetBasket = () => {
    write(local, BASKET_KEY, null)
    set({ basket: null })
  }

  const refreshBasket = async () => {
    const { api, basket } = get()
    if (!api || !basket) return
    try {
      const fresh = await api.basket(basket.ident)
      if (fresh.complete) forgetBasket()
      else set({ basket: fresh })
    } catch (error) {
      if (error instanceof StoreError && (error.status === 404 || error.status === 422))
        forgetBasket()
    }
  }

  const ensureBasket = async (): Promise<Basket> => {
    const { api, basket } = get()
    if (!api) throw new StoreError(0, 'Lo store è chiuso.')
    if (basket) return basket
    const created = await api.createBasket(absolute('/grazie'), absolute('/'))
    write(local, BASKET_KEY, created.ident)
    set({ basket: created })
    return created
  }

  const putIn = async (id: number, type: Buyable) => {
    const { api } = get()
    const basket = await ensureBasket()
    if (!api) return
    const updated = await api.add(basket.ident, id, type)
    set({ basket: updated, drawer: true })
  }

  return {
    token: undefined,
    failed: false,
    api: null,
    webstore: null,
    categories: undefined,
    basket: null,
    busy: null,
    drawer: false,
    signIn: null,
    notice: null,

    boot: async () => {
      // Once per page: a second run would add the pending package twice.
      if (booted) return
      booted = true
      let token: string | null
      try {
        token = await readToken()
      } catch {
        set({ failed: true, token: null })
        return
      }
      if (!token) {
        set({ token: null })
        return
      }
      const api = headless(token)
      set({ token, api })

      const ident = read(local, BASKET_KEY)
      const basket = ident ? await api.basket(ident).catch(() => null) : null
      if (basket && !basket.complete) set({ basket })
      else if (ident) write(local, BASKET_KEY, null)

      const [webstore, categories] = await Promise.allSettled([
        api.webstore(),
        api.categories(basket && !basket.complete ? basket.ident : null),
      ])
      set({
        webstore: webstore.status === 'fulfilled' ? webstore.value : null,
        categories: categories.status === 'fulfilled' ? categories.value : [],
        failed: categories.status === 'rejected',
      })

      // Back from signing in: the package picked before goes in now.
      const pending = read(session, PENDING_KEY)
      write(session, PENDING_KEY, null)
      const current = get().basket
      if (pending && current?.username) {
        try {
          const { id, type } = JSON.parse(pending) as Pending
          set({ busy: `add:${id}` })
          await putIn(id, type)
          get().say('Aggiunto al carrello.')
        } catch (error) {
          get().say(message(error), 'error')
        } finally {
          set({ busy: null })
        }
      }
    },

    setDrawer: (open) => set({ drawer: open }),
    closeSignIn: () => set({ signIn: null }),

    say: (text, tone = 'ok') => {
      clearTimeout(noticeTimer)
      set({ notice: { text, tone } })
      noticeTimer = setTimeout(() => set({ notice: null }), 4200)
    },

    add: async (pkg, type) => {
      set({ busy: `add:${pkg.id}` })
      try {
        const basket = await ensureBasket()
        if (!basket.username) {
          set({ signIn: { pkg, type } })
          return
        }
        await putIn(pkg.id, type)
        get().say(`${pkg.name} è nel carrello.`)
      } catch (error) {
        get().say(message(error), 'error')
      } finally {
        set({ busy: null })
      }
    },

    goSignIn: async () => {
      const { api, signIn } = get()
      if (!api) return
      set({ busy: 'signin' })
      try {
        const basket = await ensureBasket()
        if (signIn?.pkg) {
          write(session, PENDING_KEY, JSON.stringify({ id: signIn.pkg.id, type: signIn.type }))
        }
        const options = await api.auth(basket.ident, window.location.href)
        const choice = options.find((option) => /fivem|cfx/i.test(option.name)) ?? options[0]
        if (!choice) throw new StoreError(0, 'Tebex non offre un modo per accedere.')
        window.location.assign(choice.url)
      } catch (error) {
        write(session, PENDING_KEY, null)
        set({ busy: null })
        get().say(message(error), 'error')
      }
    },

    remove: async (id) => {
      const { api, basket } = get()
      if (!api || !basket) return
      set({ busy: `line:${id}` })
      try {
        set({ basket: await api.remove(basket.ident, id) })
      } catch (error) {
        get().say(message(error), 'error')
      } finally {
        set({ busy: null })
      }
    },

    setQuantity: async (id, quantity) => {
      const { api, basket } = get()
      if (!api || !basket) return
      if (quantity < 1) return get().remove(id)
      set({ busy: `line:${id}` })
      try {
        set({ basket: await api.quantity(basket.ident, id, quantity) })
      } catch (error) {
        get().say(message(error), 'error')
        await refreshBasket()
      } finally {
        set({ busy: null })
      }
    },

    applyCode: async (kind, code) => {
      const { api, basket } = get()
      if (!api || !basket || !code.trim()) return false
      set({ busy: 'code' })
      try {
        if (kind === 'coupon') await api.coupon(basket.ident, code.trim())
        else await api.creator(basket.ident, code.trim())
        await refreshBasket()
        get().say(kind === 'coupon' ? 'Codice sconto applicato.' : 'Codice creator applicato.')
        return true
      } catch (error) {
        get().say(message(error), 'error')
        return false
      } finally {
        set({ busy: null })
      }
    },

    dropCode: async (kind, code) => {
      const { api, basket } = get()
      if (!api || !basket) return
      set({ busy: 'code' })
      try {
        if (kind === 'coupon') await api.dropCoupon(basket.ident, code)
        else await api.dropCreator(basket.ident)
        await refreshBasket()
      } catch (error) {
        get().say(message(error), 'error')
      } finally {
        set({ busy: null })
      }
    },

    startOver: () => {
      forgetBasket()
      set({ drawer: false })
      get().say('Carrello svuotato: al prossimo acquisto potrai accedere con un altro account.')
    },

    checkout: async () => {
      const { basket } = get()
      if (!basket || basket.packages.length === 0) return
      set({ busy: 'checkout' })
      try {
        // Tebex.js is big and only the paying need it.
        const { default: Tebex } = await import('@tebexio/tebex.js')
        let paid = false
        Tebex.checkout.init({
          ident: basket.ident,
          theme: 'dark',
          locale: 'it_IT',
          colors: { primary: '#3b6fe0', secondary: '#8fb8ff' },
          closeOnPaymentComplete: true,
          popupOnMobile: true,
        })
        const offPaid = Tebex.checkout.on('payment:complete', () => {
          paid = true
        })
        const offClose = Tebex.checkout.on('close', () => {
          offPaid()
          offClose()
          if (paid) {
            forgetBasket()
            set({ drawer: false })
            navigate('/grazie')
          } else {
            void refreshBasket()
          }
        })
        set({ drawer: false })
        await Tebex.checkout.launch()
      } catch (error) {
        get().say(message(error), 'error')
      } finally {
        set({ busy: null })
      }
    },
  }
})

/** How many things are in the basket, counting quantities. */
export function useBasketCount(): number {
  return useStore(
    (state) =>
      state.basket?.packages.reduce((sum, line) => sum + (line.in_basket.quantity || 1), 0) ?? 0,
  )
}
